import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import JoditEditor from 'jodit-react';
import firebase from 'firebase/compat/app';
import { db } from '../../firebase';
import { firebaseAuth } from '../../providers/AuthProvider';
import usePageLoadingScreen from '../../hooks/usePageLoadingScreen';
import { buildDealerOptions, filterDealerOption } from '../../utils/dealerOptions';

const inputClass = 'mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100';
const buttonClass = 'rounded-md bg-zinc-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40 dark:bg-white dark:text-zinc-900';
const secondaryClass = 'rounded-md border border-zinc-300 px-4 py-2 text-sm disabled:opacity-40 dark:border-zinc-700';
const programs = ['National', 'Area South/Central', 'Area East', 'Area West', 'Dealer', 'Other'];
const optionDescriptions = {
  schedule: [
    'During event sends eligible records while the event is active.',
    'End of event sends records after the end date.',
    'Ongoing continues processing without an end date.',
  ],
  leadTrigger: [
    'Registration opt-in creates leads only for people who opt in.',
    'Checked-in creates leads for checked-in or completed participants.',
    'IH only records interactions without creating sales leads.',
  ],
  dealerMode: [
    'Postal code routes by the customer address.',
    'One dealer sends every registration to a selected Porsche Center.',
    'Selected dealers limits the customer choices.',
    'All dealers permits any Porsche Center or no preference.',
  ],
};
const fields = [
  'program', 'eventName', 'eventDate', 'eventEndDate', 'drivingEvent',
  'leadTrigger', 'modelInterest', 'purchaseTimeFrame', 'porscheExperience',
  'dealerMode', 'dealerCodes', 'schedule', 'requesterName', 'requesterEmail',
  'requesterDepartment', 'core12ApproverEmails', 'crmApproverEmails',
  'leadCampaignCode', 'leadCampaignDescription', 'ihCampaignCode',
  'ihCampaignDescription', 'campaignComments', 'externalUse',
  'leadNotNeeded', 'ihNotNeeded',
];
const makeDefault = (id) => ({
  id, program: '', eventName: '', eventDate: '', eventEndDate: '',
  drivingEvent: true, leadTrigger: 'optIn', modelInterest: true,
  purchaseTimeFrame: true, porscheExperience: false, dealerMode: 'postal',
  dealerCodes: [], schedule: 'during', requesterName: '',
  requesterEmail: '', requesterDepartment: '', core12ApproverEmails: [],
  crmApproverEmails: [], leadCampaignCode: '', leadCampaignDescription: '',
  ihCampaignCode: '', ihCampaignDescription: '', externalUse: false,
  campaignComments: '',
  leadNotNeeded: false, ihNotNeeded: false, status: 'draft',
});
const selectedApprovers = (request, stage) => {
  const values = request?.[stage + 'ApproverEmails'];
  return Array.isArray(values) ? values : request?.[stage + 'ApproverEmail']
    ? [request[stage + 'ApproverEmail']] : [];
};
const dateTime = (value) => {
  const ms = typeof value === 'number' ? value : value?.toMillis?.() ||
    (value?.seconds ? value.seconds * 1000 : null);
  return ms ? new Date(ms).toLocaleString() : 'Unknown';
};
const labelFor = (type) => ({
  public_submitted: 'Public request submitted',
  request_created: 'Request created', request_saved: 'Request saved',
  revision_saved: 'New revision saved', crm_review_requested: 'CRM review requested',
  crm_approved: 'CRM approved', event_linked: 'Linked to event',
  event_unlinked: 'Disconnected from event',
  ezentrum_setup_confirmed: 'Setup in eZentrum confirmed',
  note_added: 'Note added', notice_sent: 'Email accepted by provider',
  notice_failed: 'Email send failed', notice_skipped: 'Email skipped by Core Twelve admin',
  requester_link_issued: 'Retired requester link',
})[type] || String(type || '').replaceAll('_', ' ');
const noticeStageLabel = {
  saved: 'Draft saved', revised: 'New revision saved', submitted: 'Public submission',
  crm: 'CRM review', approved: 'CRM approval', linked: 'Event linked',
  setup: 'eZentrum setup', deleted: 'Draft deleted',
};

export default function CampaignCodeRequests({ publicMode = false }) {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(firebaseAuth);
  const [request, setRequest] = useState(null);
  const [saved, setSaved] = useState(null);
  const [approvers, setApprovers] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [linkedEvents, setLinkedEvents] = useState([]);
  const [disconnectEvent, setDisconnectEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [website, setWebsite] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [otherProgramSelected, setOtherProgramSelected] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [skipStepEmail, setSkipStepEmail] = useState(false);
  const isCore12Admin = Number(user?.role) === 1 &&
    /@core12\.com$/i.test(user?.email || '');
  const crmAccess = Number(user?.role) === 1 || user?.crmManager === true;
  const core12 = Number(user?.role) <= 2 && /@core12\.com$/i.test(user?.email || '');
  const canEdit = !publicMode && crmAccess;
  const canNote = !publicMode && !!saved && (crmAccess ||
    (core12 && selectedApprovers(request, 'core12').includes(user?.email?.toLowerCase())));
  const selectedCRMApprover = selectedApprovers(request, 'crm')
    .some((address) => String(address || '').trim().toLowerCase() ===
      user?.email?.trim().toLowerCase());
  const canApprove = !publicMode && (isCore12Admin ||
    (crmAccess && selectedCRMApprover));
  const changed = !!saved && fields.some((key) =>
    JSON.stringify(request?.[key] ?? null) !== JSON.stringify(saved?.[key] ?? null));
  const status = request?.status || 'draft';
  const linkEventId = new URLSearchParams(location.search).get('linkEvent');
  const approvalNeedsOverride = isCore12Admin &&
    !(status === 'crm_pending' && selectedCRMApprover);
  const hasOverrideReason = !!note.replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#160;/gi, ' ').trim();
  const actionBlocked = busy || changed || (!!id && request?.id !== id);
  const programChoice = otherProgramSelected ? 'Other' : request?.program || '';
  const noteConfig = useMemo(() => ({ readonly: !(canNote || isCore12Admin), toolbarAdaptive: false,
    buttons: ['bold', 'italic', 'underline', 'ul', 'ol', 'link', 'undo', 'redo'],
    height: 180 }), [canNote, isCore12Admin]);
  usePageLoadingScreen(!!request, id || 'new-campaign-request');

  const invoke = async (route, data, done) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const result = await firebase.functions().httpsCallable('appV2/' + route)(data);
      setMessage(result.data?.message || 'Saved.');
      try {
        await done?.(result.data);
      } catch {
        setError('The action completed, but the page could not refresh. Reload to see its latest status.');
      }
      return true;
    } catch (failure) {
      setError(failure?.message || 'The request could not be completed.');
      return false;
    } finally { setBusy(false); }
  };
  const loadActivity = async (requestId) => {
    if (publicMode || !requestId) return;
    try {
      const result = await firebase.functions()
        .httpsCallable('appV2/getCampaignRequestWorkflow')({ id: requestId });
      setHistory(result.data?.entries || []);
      setNotes(result.data?.notes || []);
      setLinkedEvents(result.data?.linkedEvents || []);
    } catch { setError('Could not load request activity.'); }
  };
  const refresh = async (requestId = request?.id) => {
    const snapshot = await db.collection('campaigncodes').doc(requestId).get();
    if (!snapshot.exists) throw new Error('Request not found.');
    const next = { ...makeDefault(requestId), ...snapshot.data(), id: requestId,
      core12ApproverEmails: selectedApprovers(snapshot.data(), 'core12'),
      crmApproverEmails: selectedApprovers(snapshot.data(), 'crm') };
    setRequest(next);
    setSaved(next);
    setOtherProgramSelected(!!next.program &&
      !programs.slice(0, -1).includes(next.program));
    await loadActivity(requestId);
  };
  const queueEmailAction = async (route, data, done, needsOverride = false) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const plan = publicMode ? { stage: 'submitted', recipients: ['support@core12.com'],
        subject: `Porsche eZentrum Campaign Code Request: ${data.eventName || 'Untitled event'}`,
        willSend: true, canEditRecipients: false, canSkipEmail: false } :
        (await firebase.functions().httpsCallable('appV2/previewCampaignNotice')({
          action: route, payload: data,
        })).data;
      setPendingAction({ route, data, done, needsOverride, plan,
        recipients: plan.recipients,
        skipEmail: isCore12Admin && plan.canSkipEmail && plan.willSend && skipStepEmail });
    } catch (failure) {
      setError(failure?.message || 'Could not preview this notification.');
    } finally { setBusy(false); }
  };
  const confirmEmailAction = async () => {
    if (!pendingAction) return;
    const { route, data, done, needsOverride, plan, recipients, skipEmail } = pendingAction;
    const notification = publicMode ? {} : {
      ...(plan.canEditRecipients ? { recipients } : {}),
      ...(skipEmail ? { skipEmail: true } : {}),
    };
    const payload = { ...data,
      ...(!publicMode ? { notification } : {}),
      ...(needsOverride ? { overrideNote: note } : {}),
    };
    const succeeded = await invoke(route, payload, async (body) => {
      if (needsOverride) setNote('');
      await done?.(body);
    });
    if (succeeded) {
      setPendingAction(null);
      setSkipStepEmail(false);
    }
  };
  const runStep = queueEmailAction;
  useEffect(() => {
    setSkipStepEmail(false);
    if (id && !publicMode) refresh(id).catch(() => setError('Could not load request.'));
    else {
      setRequest(makeDefault(db.collection('campaigncodes').doc().id));
      setSaved(null);
      setOtherProgramSelected(false);
    }
    db.collection('dealers').get().then((snapshot) =>
      setDealers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))))
      .catch(() => setDealers([]));
    if (!publicMode && crmAccess) firebase.functions().httpsCallable('appV2/approverList')({})
      .then((result) => setApprovers(result.data || []))
      .catch(() => setError('Could not load approvers.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, publicMode, crmAccess]);

  useEffect(() => {
    let current = true;
    setSelectedEvent(null);
    if (linkEventId && !publicMode) {
      db.collection('events').doc(linkEventId).get().then((snapshot) => {
        if (!current) return;
        if (!snapshot.exists) throw new Error('Selected event not found.');
        setSelectedEvent({ ...snapshot.data(), id: snapshot.id });
      }).catch(() => { if (current) setError('Could not load the selected event. Return to the event and reload it.'); });
    }
    return () => { current = false; };
  }, [linkEventId, publicMode]);

  const set = (key, value) => setRequest((current) => ({ ...current, [key]: value }));
  const field = (key, label, type = 'text', disabled = false) =>
    <label className="block text-sm" key={key}>{label}
      <input className={inputClass} type={type} value={request?.[key] || ''}
        disabled={disabled || busy || (!publicMode && !canEdit)}
        onChange={(event) => set(key, event.target.value)} />
    </label>;
  const toggle = (key, label, disabled = false) =>
    <label className="campaign-request-toggle text-sm" key={key}>
      <input type="checkbox" checked={request?.[key] === true}
        disabled={disabled || busy || (!publicMode && !canEdit)}
        onChange={(event) => setRequest((current) => ({ ...current,
          [key]: event.target.checked,
          ...(key === 'externalUse' && !event.target.checked ? {
            ihNotNeeded: false, leadNotNeeded: current.leadTrigger === 'none',
          } : {}),
        }))} /><span>{label}</span>
    </label>;
  const select = (key, label, options) =>
    <div className="text-sm" key={key}><label>{label}
      <select className={inputClass} value={request?.[key] || ''}
        disabled={busy || (!publicMode && !canEdit)}
        onChange={(event) => setRequest((current) => ({ ...current,
          [key]: event.target.value,
          ...(key === 'leadTrigger' ? {
            leadNotNeeded: event.target.value === 'none',
            ...(event.target.value === 'none' ? {
              leadCampaignCode: '', leadCampaignDescription: '',
            } : {}),
          } : {}),
          ...(key === 'schedule' && event.target.value === 'ongoing' ?
            { eventEndDate: '' } : {}),
          ...(key === 'dealerMode' ? { dealerCodes: [] } : {}),
        }))}>
        {options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}
      </select>
    </label>
    {optionDescriptions[key] && <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-300">
      {optionDescriptions[key].join(' ')}</p>}</div>;
  const approverSelect = (stage, label, options) =>
    <div className="text-sm" key={stage}><label>{label}</label>
      <Select classNamePrefix="manage-select" isMulti isSearchable isClearable
        options={options.map((entry) => ({ value: entry.name, label: entry.name }))}
        value={selectedApprovers(request, stage).map((email) => ({ value: email, label: email }))}
        isDisabled={!canEdit || busy}
        onChange={(items) => set(stage + 'ApproverEmails',
          (items || []).map((item) => item.value))} />
    </div>;
  if (!request) return null;
  const reviewOpened = ['crm_pending', 'approved'].includes(status);
  const approved = status === 'approved';
  const reviewWasSent = (request.crmReviewRequestedRevision != null &&
    request.crmReviewRequestedRevision === request.revision) ||
    history.some((entry) => entry.type === 'crm_review_requested' &&
      entry.revision === request.revision);
  const reviewSkipped = approved && request.crmApproval?.override === true &&
    !reviewWasSent;
  const setupComplete = request.externalUse || (linkedEvents.length > 0 &&
    linkedEvents.every((event) => event.setup?.revision === request.revision));
  const stageDone = [!!saved && !changed, reviewOpened && !changed,
    approved && !changed, approved && setupComplete && !changed];
  const currentStage = stageDone.findIndex((done) => !done);
  const stageClass = (index) => `rounded-lg border p-4 ${index === 1 && reviewSkipped ?
    'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30' :
    stageDone[index] ?
    'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950/30' :
    currentStage === index ?
      'border-zinc-900 bg-white dark:border-zinc-100 dark:bg-zinc-900' :
      'border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900/40'}`;

  return <main className={`mx-auto space-y-6 pb-10 text-zinc-900 dark:text-zinc-100 ${publicMode ? 'max-w-5xl' : 'max-w-7xl'}`}>
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div><h1 className="text-2xl font-semibold">Campaign Code Request</h1>
        {!publicMode && <p className="text-sm">Status: {status.replaceAll('_', ' ')}.
          Revision {request.revision || 0}.</p>}</div>
      {!publicMode && <button className={secondaryClass} type="button"
        onClick={() => navigate('/manage/campaign-codes')}>Back to requests</button>}
    </div>
    <div className={`grid items-start gap-6 ${publicMode ? '' : 'xl:grid-cols-[300px_minmax(0,1fr)]'}`}>
    {!publicMode && <aside aria-label="Campaign request progress"
      className="min-w-0 space-y-4 rounded-lg border border-zinc-300 bg-zinc-50 p-4 break-words dark:border-zinc-700 dark:bg-zinc-950">
      <div><h2 className="text-xl font-semibold">Approval journey</h2>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          Work through each step. This request is {status.replaceAll('_', ' ')}.
          {saved && ` Revision ${request.revision || 0}.`}
        </p></div>
      {error && !pendingAction && <p role="alert" className="rounded bg-red-100 p-3 text-sm text-red-900">{error}</p>}
      {message && <p role="status" className="rounded bg-green-100 p-3 text-sm text-green-900">{message}</p>}
      {changed && <p className="rounded bg-amber-100 p-3 text-sm text-amber-900">
        Save changes first. A configuration change starts a new revision and resets approval.</p>}
      {isCore12Admin && approvalNeedsOverride && !approved &&
        <p className="text-sm text-amber-800 dark:text-amber-200">
          Approving on behalf of CRM requires a reason. <a className="underline"
            href="#override-note">Enter an approval note</a> before approving.</p>}
      <ol className="grid gap-3 pl-0!">
        <li className={stageClass(0)}>
          <p className="text-xs font-semibold uppercase tracking-wide">Step 1 · {stageDone[0] ? 'Complete' : 'Current'}</p>
          <h3 className="mt-1 text-lg font-semibold">Prepare and save</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Enter the request details, campaign codes, and approvers in the form.</p>
          {canEdit && <button type="button" className={`${buttonClass} mt-3`}
            disabled={busy}
            onClick={() => invoke('saveCC', request, async (body) => {
              setRequest(body); setSaved(body);
              if (!id) navigate('/manage/campaign-code-requests/' + body.id, { replace: true });
              else await loadActivity(body.id);
            })}>Save request</button>}
        </li>
        <li className={stageClass(1)}>
          <p className="text-xs font-semibold uppercase tracking-wide">Step 2 · {reviewSkipped ?
            'Skipped by admin override' : stageDone[1] ? 'Complete' :
              currentStage === 1 ? 'Current' : 'Upcoming'}</p>
          <h3 className="mt-1 text-lg font-semibold">Request CRM review</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Send the saved request to the selected CRM approvers.</p>
          {canEdit && ['draft', 'submitted'].includes(status) && <button type="button" className={`${buttonClass} mt-3`}
            disabled={!saved || actionBlocked || !selectedApprovers(request, 'crm').length}
            onClick={() => runStep('sendCRMCampaignReview', { id: request.id },
              () => refresh(request.id))}>Send to CRM approvers</button>}
          {!selectedApprovers(request, 'crm').length && <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
            Select a CRM approver in the request details, then save.</p>}
          {changed && <p className="mt-2 text-sm">Save changes before sending.</p>}
        </li>
        <li className={stageClass(2)}>
          <p className="text-xs font-semibold uppercase tracking-wide">Step 3 · {stageDone[2] ? 'Complete' : currentStage === 2 ? 'Current' : 'Upcoming'}</p>
          <h3 className="mt-1 text-lg font-semibold">CRM approval</h3>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {approvalNeedsOverride ? 'You can approve the saved request with an override note.' :
              'A selected CRM approver reviews the codes and approves this revision.'}</p>
          {!approved && <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            Complete the required campaign codes and descriptions before approving.</p>}
          {canApprove && !approved && <button type="button" className={`${buttonClass} mt-3`}
            disabled={!saved || busy || changed ||
              (approvalNeedsOverride && !hasOverrideReason) ||
              (!isCore12Admin && status !== 'crm_pending')}
            onClick={() => runStep('approveCRMCampaign', { id: request.id },
              () => refresh(request.id), approvalNeedsOverride)}>
              {approvalNeedsOverride ? 'Approve with admin override' : 'Approve as CRM'}</button>}
          {canApprove && !isCore12Admin && !approved && status !== 'crm_pending' &&
            <p className="mt-2 text-sm">Send the request for CRM review to unlock approval.</p>}
          {!canApprove && !approved && <p className="mt-2 text-sm">
            Only a selected CRM approver can approve this request.</p>}
        </li>
        <li className={stageClass(3)}>
          <p className="text-xs font-semibold uppercase tracking-wide">Step 4 · {stageDone[3] ? 'Complete' : currentStage === 3 ? 'Current' : 'Upcoming'}</p>
          <h3 className="mt-1 text-lg font-semibold">Event setup</h3>
          {request.externalUse ? <p className="mt-1 text-sm">External requests do not need event setup.</p> : <>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
              Link an approved request to an event, then confirm setup for this revision.</p>
            {approved && (core12 || Number(user?.role) === 1) &&
              (linkEventId ? <div className="mt-3 text-sm">
                <p>Selected event: {selectedEvent?.name || 'Loading event…'} ({linkEventId})</p>
                <p>Request: {request.eventName}</p>
                <p className="mt-1 text-amber-800 dark:text-amber-200">
                  This event is not linked yet. Linking saves the approved settings to the
                  event; selecting it in the editor did not save the link.</p>
                <button type="button" className={`${buttonClass} mt-2`}
                  disabled={actionBlocked || selectedEvent?.id !== linkEventId}
                  onClick={() => runStep('linkCampaignEvent',
                    { id: request.id, eventId: linkEventId, revision: request.revision }, async () => {
                      navigate(location.pathname, { replace: true });
                      await refresh(request.id);
                    })}>Link selected event</button>
              </div> : <button type="button" className={`${secondaryClass} mt-3`}
                onClick={() => navigate('/manage/')}>Choose an event to link</button>)}
            {linkedEvents.map((event) => <div key={event.id} className="mt-3 border-t pt-3 text-sm dark:border-zinc-700">
              <a className="underline" href={'/manage/event/' + event.id}>{event.name}</a>
              <span className="ml-2">{event.setup?.revision === request.revision ?
                'Setup confirmed' : 'Setup pending'}</span>
              {(core12 || Number(user?.role) === 1) && <button type="button"
                className={`${secondaryClass} mt-2 block`} disabled={actionBlocked}
                onClick={() => setDisconnectEvent(event)}>Disconnect {event.name}</button>}
              {core12 && approved && event.setup?.revision !== request.revision &&
                <button type="button" className={`${secondaryClass} mt-2 block`}
                  disabled={actionBlocked}
                  onClick={() => runStep('confirmCampaignSetup',
                    { id: request.id, eventId: event.id },
                    () => loadActivity(request.id))}>Confirm eZentrum setup</button>}
            </div>)}
          </>}
        </li>
      </ol>
      {disconnectEvent && <div role="dialog" aria-label="Disconnect campaign request"
        className="rounded-lg border p-4 dark:border-zinc-700">
        <h3 className="text-base font-semibold">Disconnect {disconnectEvent.name} from {request.eventName}?</h3>
        <p className="mt-2 text-sm">C@P exports will pause until an approved request is linked and setup is confirmed. Event details and campaign codes are retained for review.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={buttonClass} disabled={actionBlocked}
            onClick={() => invoke('unlinkCampaignEvent', { id: request.id,
              eventId: disconnectEvent.id, revision: disconnectEvent.revision ?? null }, async () => {
                setSkipStepEmail(false);
                setDisconnectEvent(null);
                await loadActivity(request.id);
              })}>
            Confirm disconnect
          </button>
          <button type="button" className={secondaryClass} disabled={busy}
            onClick={() => setDisconnectEvent(null)}>Cancel</button>
        </div>
      </div>}
      {isCore12Admin && approvalNeedsOverride && !approved && <div id="override-note"
        className="rounded-lg border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
        <h3 className="text-base font-semibold">CRM approval override note</h3>
        <p className="mb-3 text-sm">Explain why you are approving on behalf of CRM.
          This reason is recorded with the approval.</p>
        <JoditEditor value={note} config={noteConfig}
          onChange={(content) => setNote(content)} onBlur={(content) => setNote(content)} />
        {!hasOverrideReason && <p className="mt-2 text-sm text-amber-800 dark:text-amber-200">
          Add a note to enable approval on behalf of CRM.</p>}
        {saved && <button type="button" className={`${secondaryClass} mt-3`}
          disabled={busy || !hasOverrideReason}
          onClick={() => invoke('addCampaignNote', { id: request.id, html: note },
            async () => { setNote(''); await loadActivity(request.id); })}>
          Add a general note without taking a step</button>}
      </div>}
      {isCore12Admin && <div>
        <label className="mt-3 flex items-center gap-3 text-sm">
          <input type="checkbox" checked={skipStepEmail} disabled={busy}
            aria-describedby="skip-step-email-help"
            onChange={(event) => setSkipStepEmail(event.target.checked)} />
          Skip confirmation email for this step
        </label>
        <p id="skip-step-email-help" className="mt-1 text-xs text-amber-800 dark:text-amber-200">
          Complete the next email-reviewed step without notifying the recipients.
          The skipped email is recorded in Activity. This choice resets after the step
          completes. Saving never sends email; resending a notification always sends email.
        </p>
      </div>}
      <div className="flex flex-wrap gap-2">
        {canEdit && ['crm_pending', 'approved'].includes(status) &&
          <button type="button" className={secondaryClass} disabled={busy}
            onClick={() => queueEmailAction('resendCampaignNotice', { id: request.id },
              () => loadActivity(request.id))}>Resend notification</button>}
        {canEdit && saved && status === 'draft' &&
          <button type="button" className={secondaryClass} disabled={busy}
            onClick={() => queueEmailAction('deleteCC', { id: request.id },
              () => navigate('/manage/campaign-codes'), false)}>Delete draft</button>}
      </div>
    </aside>}
    <div className="min-w-0 space-y-6">
    {publicMode && error && !pendingAction && <p role="alert" className="rounded bg-red-100 p-3 text-red-900">{error}</p>}
    {publicMode && message && <p role="status" className="rounded bg-green-100 p-3 text-green-900">{message}</p>}
    {!publicMode && toggle('externalUse', 'External use, outside eZentrum',
      !!saved?.publicSubmission || !!linkedEvents.length)}
    <section className="grid gap-4 rounded border p-4 sm:grid-cols-2 dark:border-zinc-700">
      <h2 className="sm:col-span-2 text-lg font-semibold">Request and requester</h2>
      <div className="text-sm"><label>Program
        <select className={inputClass} value={programChoice}
          disabled={busy || (!publicMode && !canEdit)}
          onChange={(event) => {
            setOtherProgramSelected(event.target.value === 'Other');
            set('program', event.target.value);
          }}>
          <option value="">Select a program</option>
          {programs.map((program) => <option key={program} value={program}>{program}</option>)}
        </select></label>
        {programChoice === 'Other' && <label className="mt-2 block">Other program
          <input className={inputClass} value={request.program === 'Other' ? '' : request.program}
            disabled={busy || (!publicMode && !canEdit)}
            onChange={(event) => set('program', event.target.value)} /></label>}
      </div>
      {field('eventName', request.externalUse ? 'Request title' : 'Event name')}
      {field('requesterName', 'Requester name')}
      {field('requesterEmail', 'Requester email', 'email')}
      {field('requesterDepartment', 'Requester department')}
      {!publicMode && approverSelect('crm', 'CRM approvers',
        approvers.filter((entry) => entry.crmEligible))}
      {!publicMode && !request.externalUse &&
        approverSelect('core12', 'Core Twelve recipients',
          approvers.filter((entry) => /@core12\.com$/i.test(entry.name) &&
            Number(entry.role) <= 2))}
    </section>
    {!request.externalUse && <section className="grid gap-4 rounded border p-4 sm:grid-cols-2 dark:border-zinc-700">
      <h2 className="sm:col-span-2 text-lg font-semibold">Event and transmission</h2>
      {select('schedule', 'Data transmission schedule',
        [['during', 'During event'], ['end', 'End of event'], ['ongoing', 'Ongoing']])}
      {select('leadTrigger', 'Lead trigger',
        [['optIn', 'Registration opt-in'], ['checkedIn', 'Checked-in'], ['none', 'IH only']])}
      {field('eventDate', 'Start date', 'date')}
      {request.schedule !== 'ongoing' && field('eventEndDate', 'End date', 'date')}
      {select('dealerMode', 'Dealer routing',
        [['postal', 'Postal code'], ['single', 'One dealer'],
          ['multi', 'Selected dealers'], ['all', 'All dealers']])}
      {(request.dealerMode === 'single' || request.dealerMode === 'multi') &&
        <div className="text-sm"><label htmlFor="campaign-dealers">Dealers</label>
          <Select isMulti inputId="campaign-dealers" classNamePrefix="manage-select"
            options={buildDealerOptions([], dealers, { includeNoPreference: false, currentValues: request.dealerCodes || [] })}
            value={buildDealerOptions([], dealers, { includeNoPreference: false, currentValues: request.dealerCodes || [] })
              .filter((option) => (request.dealerCodes || []).includes(option.value))}
            isDisabled={busy || (!publicMode && !canEdit)} filterOption={filterDealerOption}
            placeholder="Search Porsche Center, city, state or postal code"
            onChange={(options) => set('dealerCodes', (options || []).map((option) => option.value))} />
        </div>}
      <div className="space-y-2 sm:col-span-2">
        {toggle('drivingEvent', 'Driving event')}
        {request.leadTrigger === 'checkedIn' ? <p className="text-sm">
          Checked-in leads do not ask for dealer contact opt-in. Configure model interest,
          purchase timeframe, and Porsche experience in the event Data Field settings.
        </p> : <>
        {toggle('modelInterest', 'Collect model interest', request.leadTrigger === 'optIn')}
        {toggle('purchaseTimeFrame', 'Collect purchase timeframe',
          request.leadTrigger === 'optIn')}
        {toggle('porscheExperience', 'Collect Porsche experience')}
        </>}
      </div>
    </section>}
    {!publicMode && <section className="grid gap-4 rounded border p-4 sm:grid-cols-2 dark:border-zinc-700">
      <h2 className="sm:col-span-2 text-lg font-semibold">Campaign codes</h2>
      <p className="sm:col-span-2 text-sm text-zinc-600 dark:text-zinc-300">
        Naming guidance: start with PCNA-M-, followed by N, AS, AE, AW, or D for
        National, South/Central, East, West, or Dealer.</p>
      <div>
        {toggle('leadNotNeeded', 'Lead code not needed', !request.externalUse)}
        {!request.externalUse && <p className="mt-1 text-xs">
          {request.leadTrigger === 'none' ? 'IH only does not need a Lead code.' :
            'A Lead code is required for this trigger. Choose IH only to omit it.'}
        </p>}
      </div>
      <div>
        {toggle('ihNotNeeded', 'IH code not needed', !request.externalUse)}
        {!request.externalUse && <p className="mt-1 text-xs">
          An IH code is required for every eZentrum event.
        </p>}
      </div>
      {!request.leadNotNeeded && request.leadTrigger !== 'none' && <>
        {field('leadCampaignCode', 'Lead campaign code')}
        {field('leadCampaignDescription', 'Lead campaign description')}
      </>}
      {!request.ihNotNeeded && <>
        {field('ihCampaignCode', 'Interaction History code')}
        {field('ihCampaignDescription', 'Interaction History description')}
      </>}
      <div className="sm:col-span-2">
        {field('campaignComments', 'C@P export comments')}
      </div>
      <p className="sm:col-span-2 text-sm text-zinc-600 dark:text-zinc-300">
        eZentrum always requires an IH code. External requests need at least one complete
        code and description pair.</p>
    </section>}
    {publicMode && <label className="hidden" aria-hidden="true">Website
      <input tabIndex="-1" value={website}
        onChange={(event) => setWebsite(event.target.value)} /></label>}
    {publicMode && <button type="button" className={buttonClass}
      disabled={busy || submitted} onClick={() => queueEmailAction('submitCampaignCodeRequest',
        { ...request, website }, () => setSubmitted(true))}>Submit request</button>}
    {pendingAction && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="presentation">
      <section role="dialog" aria-modal="true" aria-labelledby="campaign-email-review-title"
        className="w-full max-w-lg space-y-4 rounded-xl bg-white p-6 shadow-xl dark:bg-zinc-900">
        <div>
          <h2 id="campaign-email-review-title" className="text-xl font-semibold">
            {pendingAction.route === 'deleteCC' ? 'Delete draft and review email' :
              'Review email before continuing'}</h2>
          <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-300">
            {noticeStageLabel[pendingAction.plan.stage] || 'Campaign request'} ·
            {' '}{pendingAction.skipEmail ? 'This action will complete without sending email.' :
              pendingAction.plan.willSend ? 'The email will be sent after this action completes.' :
              'No details changed, so this action will not send an email.'}</p>
          {pendingAction.plan.willSend && <p className="mt-2 text-sm">
            <strong>Subject:</strong> {pendingAction.plan.subject}</p>}
        </div>
        {pendingAction.plan.willSend && <>
          <div>
            <h3 className="text-sm font-semibold">To</h3>
            <div className="mt-2 space-y-2 rounded-md border p-3 text-sm dark:border-zinc-700">
              {pendingAction.plan.recipients.map((address) =>
                <label key={address} className="flex items-center gap-3">
                  {pendingAction.plan.canEditRecipients && <input type="checkbox"
                    checked={pendingAction.recipients.includes(address)} disabled={busy || pendingAction.skipEmail}
                    onChange={(event) => setPendingAction((current) => ({ ...current,
                      recipients: event.target.checked ? [...current.recipients, address] :
                        current.recipients.filter((item) => item !== address),
                    }))} />}
                  <span>{address}</span>
                </label>)}
            </div>
            {pendingAction.plan.canEditRecipients && <p className="mt-2 text-xs text-zinc-600 dark:text-zinc-300">
              Select who receives this email. To add a different address, cancel, edit the requester or approvers,
              save the request, then return to this step.</p>}
          </div>
          {pendingAction.plan.canSkipEmail && <label className="flex items-center gap-3 text-sm">
            <input type="checkbox" checked={pendingAction.skipEmail} disabled={busy}
              onChange={(event) => {
                const skipEmail = event.target.checked;
                setSkipStepEmail(skipEmail);
                setPendingAction((current) => ({ ...current, skipEmail }));
              }} />
            Skip this email as Core Twelve admin
          </label>}
          {pendingAction.needsOverride && <label className="block text-sm">
            CRM approval override note
            <textarea className={inputClass} aria-label="Reason for approving on behalf of CRM"
              value={note.replace(/<[^>]*>/g, '')} disabled={busy}
              onChange={(event) => setNote(event.target.value)} />
            <span className="mt-1 block text-xs text-amber-800 dark:text-amber-200">
              This reason is saved in Notes and recorded with the CRM approval override.
            </span>
          </label>}
        </>}
        {error && <p role="alert" className="rounded bg-red-100 p-3 text-sm text-red-900">{error}</p>}
        {busy && <p role="status" className="text-sm font-semibold">
          {pendingAction.skipEmail ? 'Completing action without email…' :
            `Completing action and sending email to ${pendingAction.recipients.join(', ')}…`}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" className={secondaryClass} disabled={busy}
            onClick={() => setPendingAction(null)}>Cancel</button>
          <button type="button" className={buttonClass}
            disabled={busy || (pendingAction.plan.willSend && !pendingAction.skipEmail &&
              !pendingAction.recipients.length) || (pendingAction.needsOverride && !hasOverrideReason)}
            onClick={confirmEmailAction}>Continue</button>
        </div>
      </section>
    </div>}
    {!publicMode && saved && <section className="space-y-4 rounded border p-4 dark:border-zinc-700">
      <h2 className="text-lg font-semibold">Notes</h2>
      {notes.map((entry) => <article key={entry.id} id={'note-' + entry.id}
        className="rounded border p-3 text-sm dark:border-zinc-700">
        <p className="text-zinc-600 dark:text-zinc-300">
          {entry.kind === 'process_override' && <strong>
            Core Twelve admin override · {labelFor(entry.action)} · </strong>}
          {entry.actorEmail} · {dateTime(entry.at)}</p>
        <div className="prose dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: entry.html }} />
      </article>)}
      {canNote && (!isCore12Admin || !approvalNeedsOverride || approved) && <><JoditEditor value={note} config={noteConfig}
        onChange={(content) => setNote(content)} onBlur={(content) => setNote(content)} />
        <button type="button" className={secondaryClass} disabled={busy || !note.trim()}
          onClick={() => invoke('addCampaignNote', { id: request.id, html: note },
            async () => { setNote(''); await loadActivity(request.id); })}>
          Add note</button></>}
    </section>}
    {!publicMode && saved && <section className="space-y-2 rounded border p-4 text-sm dark:border-zinc-700">
      <h2 className="text-lg font-semibold">Links</h2>
      <p>CRM approvers open the current request in Manage:</p>
      <a className="break-all underline" href={`/manage/campaign-code-requests/${request.id}`}>
        {window.location.origin}/manage/campaign-code-requests/{request.id}
      </a>
      {history.filter((entry) => entry.type === 'requester_link_issued')
        .map((entry) => <p key={entry.id} className="text-zinc-600 dark:text-zinc-300">
          Retired requester link · {entry.recipient || 'Unknown recipient'} ·
          {' '}{entry.delivery || 'delivery unknown'} · expires {dateTime(entry.expiresAt)}
          {entry.endedAt && ` · ended ${dateTime(entry.endedAt)}`}
        </p>)}
    </section>}
    {!publicMode && saved && <section className="space-y-3 rounded border p-4 dark:border-zinc-700">
      <h2 className="text-lg font-semibold">Activity</h2>
      <p className="text-xs text-zinc-600 dark:text-zinc-300">An accepted email may still be
        filtered by the recipient's mail system.</p>
      <ol className="space-y-2 text-sm">{history.map((entry) =>
        <li key={entry.id} className="border-l-2 border-zinc-300 pl-3 dark:border-zinc-700">
          <strong>{labelFor(entry.type)}</strong> · {dateTime(entry.at)}
          {entry.stage && ' · ' + (noticeStageLabel[entry.stage] || entry.stage)}
          {entry.actorEmail && ' · ' + entry.actorEmail}
          {entry.override && <> · <a className="underline" href={'#note-' + entry.overrideNoteId}>
            Core Twelve admin override note</a></>}
          {entry.eventId && ' · Event ' + entry.eventId}
          {entry.recipients && <div>{entry.type === 'notice_skipped' ?
            'Would have sent to' : 'To'}: {entry.recipients.join(', ')}</div>}
          {entry.recipient && <div>To: {entry.recipient}</div>}
          {entry.delivery && <div>Delivery: {entry.delivery}</div>}
          {entry.expiresAt && <div>Link expired: {dateTime(entry.expiresAt)}</div>}
          {entry.lastOpenedAt && <div>Link last opened: {dateTime(entry.lastOpenedAt)}</div>}
          {entry.endedAt && <div>Link ended: {dateTime(entry.endedAt)}</div>}
        </li>)}</ol>
    </section>}
    </div>
    </div>
  </main>;
}
